/**
 * SetAsideReasonModal — iter99 · Modal obligatoire pour écarter un candidat.
 *
 * **Contexte CEO 2026-09-03** :
 * > « J'aimerais que tu donnes la possibilité quand on clique sur "Écarter
 * > ce candidat de la file", qu'un modèle s'ouvre pour choisir / écrire
 * > le motif pour laquelle on a écarté cette personne, et que cette
 * > mention-là puisse figurer en grand directement sur la ligne du
 * > candidat concerné. »
 *
 * Miroir du RejectReasonModal mais avec 6 motifs légers (écarter n'est
 * pas définitif — c'est un "je le sors de la file MAINTENANT").
 *
 * Props:
 *   - open, onOpenChange
 *   - candidate {first_name, last_name}
 *   - onConfirm(reason, note) — appelé après validation
 */
import * as Dialog from "@radix-ui/react-dialog";
import { Archive, Loader2, X } from "lucide-react";
import { useEffect, useState } from "react";

import { api } from "../../lib/api";


export default function SetAsideReasonModal({ open, onOpenChange, candidate, onConfirm }) {
    const [reason, setReason] = useState("");
    const [note, setNote] = useState("");
    const [catalog, setCatalog] = useState([]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        setReason("");
        setNote("");
        api.get("/candidates/set-aside-reasons")
            .then(({ data }) => setCatalog(data?.reasons || []))
            .catch(() => setCatalog([]));
    }, [open]);

    const canConfirm = Boolean(reason) && !busy;

    const submit = async () => {
        if (!canConfirm) return;
        setBusy(true);
        try {
            await onConfirm?.(reason, note.trim() || null);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog.Root open={open} onOpenChange={onOpenChange}>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-[100] bg-shariti-black/60 backdrop-blur-sm" />
                <Dialog.Content
                    data-testid="set-aside-reason-modal"
                    className="fixed left-1/2 top-1/2 z-[101] w-[92vw] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl bg-white shadow-2xl focus:outline-none max-h-[92vh] overflow-hidden flex flex-col"
                >
                    <div className="p-5 pb-3 border-b border-slate-100">
                        <div className="flex items-center justify-between mb-2">
                            <Dialog.Title className="text-base font-extrabold text-shariti-black inline-flex items-center gap-2">
                                <Archive className="h-4 w-4 text-amber-500" />
                                Écarter {candidate?.first_name} {candidate?.last_name} ?
                            </Dialog.Title>
                            <Dialog.Close asChild>
                                <button type="button" className="h-8 w-8 rounded-full hover:bg-slate-100 inline-flex items-center justify-center text-slate-500">
                                    <X className="h-4 w-4" />
                                </button>
                            </Dialog.Close>
                        </div>
                        <p className="text-[11px] font-semibold text-slate-500">
                            Le motif apparaîtra à côté du nom dans la liste des écartés
                            — tu pourras le remettre dans la file en 1 clic depuis /ecartes.
                        </p>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 space-y-3">
                        <div className="grid grid-cols-1 gap-1.5">
                            {catalog.map((r) => (
                                <button
                                    key={r.value}
                                    type="button"
                                    onClick={() => setReason(r.value)}
                                    data-testid={`set-aside-reason-${r.value}`}
                                    className={`text-left px-3 py-2 rounded-xl border-2 transition text-xs font-bold ${
                                        reason === r.value
                                            ? "border-amber-400 bg-amber-50 text-amber-800"
                                            : "border-slate-200 hover:border-amber-300 bg-white text-slate-700"
                                    }`}
                                >
                                    {r.label}
                                </button>
                            ))}
                        </div>

                        <div className="pt-1">
                            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 block">
                                Note libre (optionnelle)
                            </label>
                            <textarea
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                rows={2}
                                data-testid="set-aside-reason-note"
                                placeholder="Détails — ex : « rappeler la semaine prochaine, envoie CV en retard »"
                                className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm font-semibold focus:border-amber-400 focus:outline-none resize-none"
                            />
                        </div>
                    </div>

                    <div className="p-4 border-t border-slate-100 flex gap-2">
                        <button type="button" onClick={() => onOpenChange(false)}
                            className="flex-1 h-11 rounded-xl border-2 border-slate-200 hover:bg-slate-50 font-black text-sm">
                            Annuler
                        </button>
                        <button
                            type="button"
                            onClick={submit}
                            disabled={!canConfirm}
                            data-testid="set-aside-reason-submit"
                            className="flex-1 h-11 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-black text-sm inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Archive className="h-4 w-4" />}
                            {busy ? "Écartement…" : "Écarter"}
                        </button>
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
}
